/**
 * Serialisierte Read-Modify-Write für business_*_data (verhindert Lost Updates
 * zwischen Verkauf, Lots, Stock und Écart).
 */

import { mergePurchasesById } from '../../utils/businessStateSync';
import { publishDomainEvent, DOMAIN_EVENTS } from '../events/domainEventBus';
import { detectPayloadFieldChanges } from './detectPayloadFieldChanges';
import { syncAggregateIndexFromPayload } from './domainAggregateStore';
import { generateClientId } from '../../utils/frenchIdGenerator';
import { phonesMatch } from '../../utils/receiptFormat';
import { stampSaleCompletionFields } from '../../utils/saleReceiptDateTime';
import {
  businessDataStorageKey,
  ENTITY_PAYLOAD_FIELD_NAMES,
  mergeBusinessPayloadPreserveRicher,
  normalizeBusinessStorageId,
  normalizeBusinessPayloadShape,
  readBestBusinessMergedPayload,
  scoreBusinessMergedPayload
} from '../../utils/sharedCore';
import { writeBusinessMergedPayload } from './businessPayloadPersistence';
import { assertBusinessPayloadWriteAllowed } from './storageSafeMode';
import {
  appendSaleFinalizeJournalPending,
  markSaleFinalizeJournalCommitted,
  markSaleFinalizeJournalFailed
} from './storageFinalizeJournal';
import {
  isDexieAtomicBusinessWriteEnabled,
  runAtomicBusinessPersist,
  runAtomicEntityItemPersist,
  runAtomicEntityOnlyPersist,
  serializeBusinessPayloadForKvStorage
} from './dexieBusinessTransaction';
import {
  buildSaleSyncOutboxEntry
} from './outboxAccess';
import {
  BusinessPayloadWriteConflict,
  isBusinessPayloadOptimisticLockEnabled,
  readPayloadLockVersion,
  reloadFreshBusinessPayloadFromIdb
} from './businessPayloadFreshRead';

const PARTIAL_ENTITY_READ_FIELDS = new Set(ENTITY_PAYLOAD_FIELD_NAMES);
const MAX_BUSINESS_WRITE_RETRIES = 3;

/** @type {Map<string, Promise<unknown>>} */
const chains = new Map();

/** Wartet auf alle ausstehenden Business-Payload-Writes (z. B. vor Reload/Quit). */
export async function flushAllBusinessPayloadWrites() {
  const pending = [...chains.values()].filter(Boolean);
  if (pending.length > 0) {
    await Promise.allSettled(pending);
  }
}

async function flushKvBackendPendingWrites() {
  const { getStorageEngineRef } = await import('./storageEngineRef');
  const engine = getStorageEngineRef();
  if (engine._backend?.flushPendingWrites) {
    await engine._backend.flushPendingWrites();
  }
}

/** Wartet auf ausstehende IndexedDB-KV-Writes (z. B. vor App-Beenden). */
export async function flushPendingStorageWrites() {
  await flushAllBusinessPayloadWrites();
  await flushKvBackendPendingWrites();
}

function stampPayloadVersion(updated, previousParsed) {
  const prevVer = Number(previousParsed?.payloadVersion);
  const nextVer = Number.isFinite(prevVer) && prevVer >= 0 ? prevVer + 1 : 1;
  return {
    ...updated,
    payloadVersion: nextVer,
    payloadUpdatedAt: new Date().toISOString(),
    entityStorePrimary: true,
    entityStoreNoKvBlob: previousParsed?.entityStoreNoKvBlob === true ? true : undefined
  };
}

async function readLockVersionForWrite(businessId, parsed) {
  const { isEntityStoreNoKvBlobEnabled, readBusinessPayloadMeta } = await import(
    './businessPayloadMeta'
  );
  if (isEntityStoreNoKvBlobEnabled()) {
    const meta = await readBusinessPayloadMeta(businessId);
    return meta.payloadVersion;
  }
  return readPayloadLockVersion(parsed);
}

function saleSyncOutboxEntriesResolver(businessId, saleRef, mutationId) {
  return ({ payload }) => {
    const sales = Array.isArray(payload.sales) ? payload.sales : [];
    const committed =
      sales.find(
        (s) =>
          (saleRef.orderId && s.orderId === saleRef.orderId) ||
          (saleRef.id && s.id === saleRef.id)
      ) || saleRef;
    const entry = buildSaleSyncOutboxEntry(businessId, committed, mutationId);
    return entry ? [entry] : [];
  };
}

/** Non-Dexie: commit outbox after local write (Dexie TX already includes outbox). */
async function enqueueOutboxEntriesAfterPersist(outboxEntries, payload) {
  if (outboxEntries == null || isDexieAtomicBusinessWriteEnabled()) return;

  let entries;
  try {
    entries =
      typeof outboxEntries === 'function'
        ? outboxEntries({ payload })
        : outboxEntries;
  } catch (e) {
    console.warn('[businessPayloadWrite] outbox resolve failed:', e?.message || e);
    return;
  }
  if (!Array.isArray(entries) || entries.length === 0) return;

  try {
    const { enqueueOutbox } = await import('./outboxAccess');
    for (const entry of entries) {
      if (!entry?.entityType) continue;
      await enqueueOutbox(entry);
    }
  } catch (e) {
    console.warn(
      '[businessPayloadWrite] outbox enqueue after persist failed:',
      e?.message || e
    );
  }
}

/**
 * Partial entity read for scoped edits (lots/payments/…) when entity authority + authoritativeFields.
 * Only when every field is a known entity_* field (avoids wiping non-entity keys like stockConsumptions).
 * Escape hatch: options.partialEntityRead === false | true
 */
function shouldUsePartialEntityRead(options = {}) {
  if (options.partialEntityRead === false) return false;
  const fields = options.readFields || options.authoritativeFields || [];
  if (!Array.isArray(fields) || fields.length === 0) return false;
  const allEntityFields = fields.every((f) => PARTIAL_ENTITY_READ_FIELDS.has(f));
  if (options.partialEntityRead === true) return allEntityFields;
  return allEntityFields;
}

async function buildPartialWriteBase(businessId, noKvBlob) {
  const { readBusinessPayloadMeta } = await import('./businessPayloadMeta');
  const base = { entityStorePrimary: true };
  try {
    const meta = await readBusinessPayloadMeta(businessId);
    if (meta?.payloadVersion != null) {
      base.payloadVersion = meta.payloadVersion;
      base.payloadUpdatedAt = meta.payloadUpdatedAt || undefined;
    }
  } catch {
    /* meta optional when blob still holds version */
  }
  if (noKvBlob) {
    base.entityStoreNoKvBlob = true;
    return base;
  }
  try {
    const best = readBestBusinessMergedPayload(businessId);
    if (best?.parsed) {
      if (base.payloadVersion == null && best.parsed.payloadVersion != null) {
        base.payloadVersion = best.parsed.payloadVersion;
      }
      if (!base.payloadUpdatedAt && best.parsed.payloadUpdatedAt) {
        base.payloadUpdatedAt = best.parsed.payloadUpdatedAt;
      }
    }
  } catch {
    /* ignore */
  }
  return base;
}

async function parseBusinessPayload(businessId, options = {}) {
  const { isEntityStoreAuthorityEnabled } = await import('./entityAggregateStore');
  const { isEntityStoreNoKvBlobEnabled } = await import('./businessPayloadMeta');

  const readFields = options.readFields || options.authoritativeFields || [];
  const noKvBlob = isEntityStoreNoKvBlobEnabled();
  const usePartial =
    isEntityStoreAuthorityEnabled() &&
    shouldUsePartialEntityRead(options) &&
    readFields.length > 0;

  if (usePartial) {
    const { overlayEntityFieldsOnPayload } = await import('./businessPayloadAssembly');
    const base = await buildPartialWriteBase(businessId, noKvBlob);
    return overlayEntityFieldsOnPayload(businessId, readFields, base);
  }

  if (isEntityStoreAuthorityEnabled()) {
    const { readBusinessPayloadWithEntityAuthority } = await import(
      './businessPayloadAssembly'
    );
    const assembled = await readBusinessPayloadWithEntityAuthority(businessId);
    if (assembled && Object.keys(assembled).length > 0) {
      return assembled;
    }
  }

  // Avoid RMW from empty base when deferred business_*_data is still in IDB only
  try {
    const { ensureBusinessPayloadKeysLoaded } = await import('./businessPayloadFreshRead');
    await ensureBusinessPayloadKeysLoaded(businessId);
  } catch {
    /* optional */
  }

  const best = readBestBusinessMergedPayload(businessId);
  if (best.parsed) {
    return normalizeBusinessPayloadShape(best.parsed);
  }
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      '[businessPayloadWrite] Aucun blob business_*_data — merge depuis zéro (nouveau ou clé différente)',
      { businessId: String(businessId), normalized: normalizeBusinessStorageId(businessId) }
    );
  }
  return {};
}

async function flushKvBackend() {
  await flushPendingStorageWrites();
}

/**
 * Under NO_KV_BLOB, entity_* skips openTickets / drafts / restaurant tables.
 * Keep a thin KV shell so cuisine + brouillons survive reload.
 */
async function persistNoKvBlobThinShell(businessId, key, updated, blobAuthoritativeFields = []) {
  if (!key || !updated || typeof updated !== 'object') return;
  try {
    const { stripEntityFieldsForBlobSnapshot, NO_KV_BLOB_SHELL_FIELDS } = await import(
      './businessPayloadAssembly'
    );
    // Prefer the canonical thin shell — readBest may return a fat legacy blob (higher score)
    // without recipes/openTickets, which would wipe those fields on every subsequent write.
    let existing = {};
    try {
      const { readCanonicalBusinessMergedPayload } = await import('../../utils/sharedCore');
      const canon = readCanonicalBusinessMergedPayload(businessId);
      if (canon?.parsed) {
        existing = canon.parsed;
      }
    } catch {
      /* optional */
    }
    if (!existing || typeof existing !== 'object' || Object.keys(existing).length === 0) {
      existing = readBestBusinessMergedPayload(businessId)?.parsed || {};
    }
    const prevShell = stripEntityFieldsForBlobSnapshot(existing) || {};
    const nextShell = stripEntityFieldsForBlobSnapshot(updated) || {};
    const merged = {
      ...prevShell,
      ...nextShell,
      payloadVersion: updated.payloadVersion,
      payloadUpdatedAt: updated.payloadUpdatedAt,
      entityStorePrimary: true,
      entityStoreNoKvBlob: true
    };
    const entitySet = new Set(ENTITY_PAYLOAD_FIELD_NAMES);
    for (const field of blobAuthoritativeFields || []) {
      if (!entitySet.has(field) && updated[field] !== undefined) {
        merged[field] = updated[field];
      }
    }
    for (const field of NO_KV_BLOB_SHELL_FIELDS) {
      // Keep previous shell value when this write did not touch the field.
      if (updated[field] !== undefined) {
        merged[field] = updated[field];
      } else if (prevShell[field] !== undefined) {
        merged[field] = prevShell[field];
      }
    }
    const result = writeBusinessMergedPayload(key, merged, {
      authoritativeFields: [
        ...new Set([
          ...(blobAuthoritativeFields || []).filter((f) => !entitySet.has(f)),
          // Include preserved shell arrays so later merges never drop recipes/tickets.
          ...NO_KV_BLOB_SHELL_FIELDS.filter((f) => merged[f] !== undefined)
        ])
      ]
    });
    if (result && result.success === false && process.env.NODE_ENV === 'development') {
      console.warn(
        '[businessPayloadWrite] thin shell persist failed:',
        result.error || 'unknown'
      );
    }
  } catch (err) {
    console.warn(
      '[businessPayloadWrite] thin shell persist failed:',
      err?.message || err
    );
  }
}

/** Nach Blob-Write Lesecaches für domain_aggregate-Pfade aktualisieren. */
export async function refreshReadCachesForFields(businessId, changedFields, payload = null) {
  if (!changedFields?.length) return;
  const fieldSet = new Set(changedFields);
  const tasks = [];

  if (fieldSet.has('purchases')) {
    const { refreshPurchasesReadCache } = await import('../../utils/purchasesReadSource');
    tasks.push(refreshPurchasesReadCache(businessId));
  }
  if (fieldSet.has('sales')) {
    const { refreshSalesReadCache } = await import('../../utils/salesReadSource');
    tasks.push(refreshSalesReadCache(businessId));
  }
  if (fieldSet.has('lots')) {
    const { refreshLotsReadCache } = await import('../../utils/lotsReadSource');
    // Authoritative write payload wins — avoid re-reading a lagging entity index
    const preloaded = Array.isArray(payload?.lots) ? payload.lots : undefined;
    tasks.push(refreshLotsReadCache(businessId, preloaded));
  }
  if (fieldSet.has('products')) {
    const { refreshProductsReadCache } = await import('../../utils/productsReadSource');
    tasks.push(refreshProductsReadCache(businessId));
  }
  if (fieldSet.has('customers')) {
    const { refreshCustomersReadCache } = await import('../../utils/customersReadSource');
    tasks.push(refreshCustomersReadCache(businessId));
  }
  if (fieldSet.has('suppliers')) {
    const { refreshSuppliersReadCache } = await import('../../utils/suppliersReadSource');
    tasks.push(refreshSuppliersReadCache(businessId));
  }
  if (fieldSet.has('stockData')) {
    const { refreshStockDataReadCache } = await import('../../utils/stockDataReadSource');
    tasks.push(refreshStockDataReadCache(businessId));
  }
  if (fieldSet.has('invoices')) {
    const { refreshInvoicesReadCache } = await import('../../utils/invoicesReadSource');
    tasks.push(refreshInvoicesReadCache(businessId));
  }
  if (fieldSet.has('orders')) {
    const { refreshOrdersReadCache } = await import('../../utils/ordersReadSource');
    tasks.push(refreshOrdersReadCache(businessId));
  }
  if (fieldSet.has('expenses')) {
    const { refreshExpensesReadCache } = await import('../../utils/expensesReadSource');
    tasks.push(refreshExpensesReadCache(businessId));
  }
  if (fieldSet.has('invoicePayments')) {
    const { refreshInvoicePaymentsReadCache } = await import(
      '../../utils/invoicePaymentsReadSource'
    );
    tasks.push(refreshInvoicePaymentsReadCache(businessId));
  }
  if (fieldSet.has('supplierDebts')) {
    const { refreshSupplierDebtsReadCache } = await import('../../utils/supplierDebtsReadSource');
    tasks.push(refreshSupplierDebtsReadCache(businessId));
  }
  if (fieldSet.has('customerDebts')) {
    const { refreshCustomerDebtsReadCache } = await import('../../utils/customerDebtsReadSource');
    tasks.push(refreshCustomerDebtsReadCache(businessId));
  }
  if (fieldSet.has('customerInvoices')) {
    const { refreshCustomerInvoicesReadCache } = await import(
      '../../utils/customerInvoicesReadSource'
    );
    tasks.push(refreshCustomerInvoicesReadCache(businessId));
  }
  if (fieldSet.has('supermarketPayments')) {
    const { refreshSupermarketPaymentsReadCache } = await import(
      '../../utils/supermarketPaymentsReadSource'
    );
    tasks.push(refreshSupermarketPaymentsReadCache(businessId));
  }
  if (fieldSet.has('supermarketInvoices')) {
    const { refreshSupermarketInvoicesReadCache } = await import(
      '../../utils/supermarketInvoicesReadSource'
    );
    tasks.push(refreshSupermarketInvoicesReadCache(businessId));
  }
  if (fieldSet.has('expiredProducts')) {
    const { refreshExpiredProductsReadCache } = await import(
      '../../utils/expiredProductsReadSource'
    );
    tasks.push(refreshExpiredProductsReadCache(businessId));
  }
  if (fieldSet.has('notifications')) {
    const { refreshNotificationsReadCache } = await import('../../utils/notificationsReadSource');
    tasks.push(refreshNotificationsReadCache(businessId));
  }
  if (fieldSet.has('workerSessions')) {
    const { refreshWorkerSessionsReadCache } = await import(
      '../../utils/workerSessionsReadSource'
    );
    tasks.push(refreshWorkerSessionsReadCache(businessId));
  }
  if (fieldSet.has('reports')) {
    const { refreshReportsReadCache } = await import('../../utils/reportsReadSource');
    tasks.push(refreshReportsReadCache(businessId));
  }
  if (fieldSet.has('discrepancyManagement')) {
    const { refreshDiscrepancyManagementReadCache } = await import(
      '../../utils/discrepancyManagementReadSource'
    );
    tasks.push(refreshDiscrepancyManagementReadCache(businessId));
  }
  if (fieldSet.has('stockLocations')) {
    const { refreshStockLocationsReadCache } = await import('../../utils/stockLocationsReadSource');
    tasks.push(refreshStockLocationsReadCache(businessId));
  }
  if (fieldSet.has('persistentStockData')) {
    const { refreshPersistentStockDataReadCache } = await import(
      '../../utils/persistentStockDataReadSource'
    );
    tasks.push(refreshPersistentStockDataReadCache(businessId));
  }
  if (fieldSet.has('offlineQueue')) {
    const { refreshOfflineQueueReadCache } = await import('../../utils/offlineQueueReadSource');
    tasks.push(refreshOfflineQueueReadCache(businessId));
  }
  if (fieldSet.has('businessInfo')) {
    const { refreshBusinessInfoReadCache } = await import('../../utils/businessInfoReadSource');
    tasks.push(refreshBusinessInfoReadCache(businessId));
  }
  if (fieldSet.has('userInfo')) {
    const { refreshUserInfoReadCache } = await import('../../utils/userInfoReadSource');
    tasks.push(refreshUserInfoReadCache(businessId));
  }
  if (fieldSet.has('metadata')) {
    const { refreshMetadataReadCache } = await import('../../utils/metadataReadSource');
    tasks.push(refreshMetadataReadCache(businessId));
  }
  if (fieldSet.has('stockMovements')) {
    const { refreshStockMovementsReadCache } = await import(
      '../../utils/stockMovementsReadSource'
    );
    tasks.push(refreshStockMovementsReadCache(businessId));
  }

  await Promise.all(tasks);
}

async function applyBusinessPayloadWriteOnce(businessId, n, mutator, options) {
  const key = businessDataStorageKey(n);

  const { isEntityStoreNoKvBlobEnabled: noKvBlobAtStart } = await import('./businessPayloadMeta');
  const { isEntityStoreAuthorityEnabled } = await import('./entityAggregateStore');
  const noKvBlobMode = noKvBlobAtStart();
  const usePartialRead =
    isEntityStoreAuthorityEnabled() && shouldUsePartialEntityRead(options);

  if (isBusinessPayloadOptimisticLockEnabled()) {
    if (noKvBlobMode) {
      const { reloadFreshBusinessPayloadMetaFromIdb } = await import('./businessPayloadMeta');
      await reloadFreshBusinessPayloadMetaFromIdb(n);
    } else {
      await reloadFreshBusinessPayloadFromIdb(n);
    }
  }

  const parsed = await parseBusinessPayload(n, options);
  const lockVersion = await readLockVersionForWrite(n, parsed);
  const baseScore = scoreBusinessMergedPayload(parsed);
  let updated = await mutator(parsed);
  const authoritativeFields = options.authoritativeFields || [];
  const authoritativeSnap = {};
  for (const field of authoritativeFields) {
    if (updated[field] !== undefined) {
      authoritativeSnap[field] = updated[field];
    }
  }
  const newScore = scoreBusinessMergedPayload(updated);
  // Skip score-repair on partial reads — missing unloaded fields would look like data loss.
  if (!usePartialRead && baseScore > 0 && newScore < baseScore) {
    updated = mergeBusinessPayloadPreserveRicher(parsed, updated);
    for (const field of authoritativeFields) {
      if (authoritativeSnap[field] !== undefined) {
        updated[field] = authoritativeSnap[field];
      }
    }
    if (process.env.NODE_ENV === 'development') {
      console.warn('[businessPayloadWrite] Merged partial write with richer blob', {
        businessId: String(businessId),
        baseScore,
        newScore,
        repairedScore: scoreBusinessMergedPayload(updated),
        authoritativeFields
      });
    }
  }
  const { isEntityStoreNoKvBlobEnabled, readBusinessPayloadMeta } = await import(
    './businessPayloadMeta'
  );
  const noKvBlob = isEntityStoreNoKvBlobEnabled();
  const versionBase = noKvBlob
    ? {
        payloadVersion: (await readBusinessPayloadMeta(n)).payloadVersion,
        entityStoreNoKvBlob: true
      }
    : parsed;
  updated = stampPayloadVersion(updated, versionBase);
  const changedFields = detectPayloadFieldChanges(parsed, updated);
  for (const field of authoritativeFields) {
    if (field && !changedFields.includes(field)) {
      changedFields.push(field);
    }
  }

  if (isBusinessPayloadOptimisticLockEnabled() && !options.skipOptimisticLock) {
    if (noKvBlob) {
      const { reloadFreshBusinessPayloadMetaFromIdb } = await import('./businessPayloadMeta');
      const currentMeta = await reloadFreshBusinessPayloadMetaFromIdb(n);
      if (currentMeta.payloadVersion !== lockVersion) {
        throw new BusinessPayloadWriteConflict();
      }
    } else {
      await reloadFreshBusinessPayloadFromIdb(n);
      const current = await parseBusinessPayload(n);
      if (readPayloadLockVersion(current) !== lockVersion) {
        throw new BusinessPayloadWriteConflict();
      }
    }
  }

  const { stripEntityFieldsForBlobSnapshot, isEntityStoreSkipBlobFieldsEnabled } =
    await import('./businessPayloadAssembly');
  const blobSnapshot = stripEntityFieldsForBlobSnapshot(updated);
  const blobAuthoritativeFields = isEntityStoreSkipBlobFieldsEnabled()
    ? authoritativeFields.filter(
        (field) =>
          ![
            'sales',
            'purchases',
            'lots',
            'products',
            'customers',
            'suppliers',
            'stockData',
            'invoices',
            'orders',
            'expenses',
            'invoicePayments',
            'supplierDebts',
            'customerDebts',
            'customerInvoices',
            'supermarketPayments',
            'supermarketInvoices',
            'expiredProducts',
            'notifications',
            'workerSessions',
            'reports',
            'discrepancyManagement',
            'stockLocations',
            'persistentStockData',
            'offlineQueue',
            'businessInfo',
            'userInfo',
            'metadata',
            'stockMovements'
          ].includes(field)
      )
    : authoritativeFields;

  if (noKvBlob && isDexieAtomicBusinessWriteEnabled()) {
    await runAtomicEntityOnlyPersist({
      businessId: n,
      payload: updated,
      changedFields,
      outboxEntries: options.outboxEntries,
      payloadMeta: {
        payloadVersion: updated.payloadVersion,
        payloadUpdatedAt: updated.payloadUpdatedAt
      }
    });
    // Thin KV shell for openTickets/drafts/tables — entity_* has no store for these.
    await persistNoKvBlobThinShell(n, key, updated, blobAuthoritativeFields);
    await flushKvBackendPendingWrites();
  } else if (isDexieAtomicBusinessWriteEnabled()) {
    const kvRawValue = serializeBusinessPayloadForKvStorage(key, blobSnapshot);
    await runAtomicBusinessPersist({
      kvKey: key,
      kvRawValue,
      businessId: n,
      payload: updated,
      changedFields,
      outboxEntries: options.outboxEntries
    });
    await flushKvBackendPendingWrites();
  } else if (noKvBlob) {
    const { ensureEntityStoreTablesReady, syncEntityStoresFromPayload, isEntityStoreEnabled } =
      await import('./entityAggregateStore');
    const { writeBusinessPayloadMeta } = await import('./businessPayloadMeta');
    await writeBusinessPayloadMeta(n, {
      payloadVersion: updated.payloadVersion,
      payloadUpdatedAt: updated.payloadUpdatedAt,
      noKvBlob: true
    });
    if (changedFields.length > 0) {
      try {
        await syncAggregateIndexFromPayload(n, updated, changedFields);
        if (isEntityStoreEnabled()) {
          const db = await ensureEntityStoreTablesReady();
          await syncEntityStoresFromPayload(db, n, updated, changedFields);
        }
      } catch (err) {
        console.warn(
          '[businessPayloadWrite] entity-only aggregate sync failed:',
          err?.message || err
        );
      }
    }
    await persistNoKvBlobThinShell(n, key, updated, blobAuthoritativeFields);
  } else {
    const { stripEntityFieldsForBlobSnapshot } = await import('./businessPayloadAssembly');
    const blobSnapshot = stripEntityFieldsForBlobSnapshot(updated);
    const result = writeBusinessMergedPayload(key, blobSnapshot, {
      authoritativeFields: blobAuthoritativeFields
    });
    if (result && result.success === false) {
      throw new Error(result.error || 'business payload write failed');
    }
    await flushKvBackend();

    if (changedFields.length > 0) {
      try {
        await syncAggregateIndexFromPayload(n, updated, changedFields);
        const { isEntityStoreEnabled, syncEntityStoresFromPayload, ensureEntityStoreTablesReady } =
          await import('./entityAggregateStore');
        if (isEntityStoreEnabled()) {
          const db = await ensureEntityStoreTablesReady();
          await syncEntityStoresFromPayload(db, n, updated, changedFields);
        }
      } catch (err) {
        console.warn(
          '[businessPayloadWrite] aggregate / entity store sync failed:',
          err?.message || err
        );
      }
    }
  }

  const { broadcastKvKeyInvalidation, broadcastEntityPayloadInvalidation } = await import(
    './kvCacheSync'
  );
  if (changedFields.length > 0) {
    try {
      await refreshReadCachesForFields(n, changedFields, updated);
    } catch (err) {
      console.warn(
        '[businessPayloadWrite] read-cache refresh failed:',
        err?.message || err
      );
    }
    publishDomainEvent(DOMAIN_EVENTS.BUSINESS_PAYLOAD_UPDATED, {
      businessId: n,
      changedFields
    });
  }

  if (noKvBlob) {
    broadcastEntityPayloadInvalidation(n, changedFields);
  } else {
    broadcastKvKeyInvalidation(key, { businessId: n, changedFields });
  }

  if (
    process.env.NODE_ENV === 'development' &&
    changedFields.some((f) => ['suppliers', 'products', 'purchases'].includes(f))
  ) {
    try {
      const { countEntityStoresByType } = await import('./entityAggregateStore');
      const counts = await countEntityStoresByType(n);
      console.info('[businessPayloadWrite] persisted → entity_*', {
        businessId: String(n),
        changedFields,
        entityCounts: {
          suppliers: counts.suppliers ?? 0,
          products: counts.products ?? 0,
          purchases: counts.purchases ?? 0
        }
      });
    } catch {
      /* optional dev log */
    }
  }

  try {
    const { scheduleSequenceSyncAfterWrite } = await import('./sequenceSyncAfterWrite');
    scheduleSequenceSyncAfterWrite(n, changedFields);
  } catch {
    /* non-blocking */
  }

  await enqueueOutboxEntriesAfterPersist(options.outboxEntries, updated);

  return updated;
}

async function applyBusinessPayloadWriteWithRetry(businessId, n, mutator, options) {
  const maxAttempts = isBusinessPayloadOptimisticLockEnabled()
    ? MAX_BUSINESS_WRITE_RETRIES
    : 1;

  let lastConflict = null;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const isLastAttempt = attempt === maxAttempts - 1;
    try {
      return await applyBusinessPayloadWriteOnce(businessId, n, mutator, {
        ...options,
        skipOptimisticLock: isLastAttempt && maxAttempts > 1
      });
    } catch (err) {
      if (err instanceof BusinessPayloadWriteConflict) {
        lastConflict = err;
        if (process.env.NODE_ENV === 'development') {
          console.warn('[businessPayloadWrite] optimistic lock retry', {
            businessId: String(n),
            attempt: attempt + 1,
            maxAttempts
          });
        }
        continue;
      }
      throw err;
    }
  }

  throw lastConflict || new BusinessPayloadWriteConflict();
}

/**
 * Führt eine Änderung am merged Business-Payload nacheinander aus (pro Business-ID).
 * @param {string} businessId
 * @param {(parsed: object) => object|Promise<object>} mutator
 * @param {{
 *   authoritativeFields?: string[],
 *   allowInSafeMode?: boolean,
 *   outboxEntries?: import('./dexieBusinessTransaction').OutboxEntryInput[] | ((ctx: { payload: object }) => import('./dexieBusinessTransaction').OutboxEntryInput[])
 * }} [options]
 */
export function queueBusinessPayloadWrite(businessId, mutator, options = {}) {
  const n = normalizeBusinessStorageId(businessId);
  if (n == null || n === '') {
    return Promise.reject(new Error('queueBusinessPayloadWrite: missing businessId'));
  }

  try {
    assertBusinessPayloadWriteAllowed(options);
  } catch (err) {
    return Promise.reject(err);
  }

  const prev = chains.get(n) || Promise.resolve();
  const run = prev
    .then(() => applyBusinessPayloadWriteWithRetry(businessId, n, mutator, options))
    .catch((err) => {
      console.error('[businessPayloadWrite] failed:', err?.message || err);
      throw err;
    });

  chains.set(
    n,
    run.catch(() => {})
  );
  return run;
}

/** Ein Feld ersetzen (z. B. sales, lots, products) — erlaubt auch kleinere Arrays (Löschen). */
export function queueBusinessPayloadFieldWrite(businessId, field, value) {
  return queueBusinessPayloadWrite(
    businessId,
    (parsed) => {
      parsed[field] = value;
      return parsed;
    },
    { authoritativeFields: [field] }
  );
}

/**
 * Phase C — single entity item upsert (NO_KV_BLOB + entity store).
 * Puts one row without full-array sync/prune. Uses the same per-business write chain.
 *
 * @param {string} businessId
 * @param {string} field  aggregate field name (e.g. 'products')
 * @param {object} item   merged entity payload for that id
 * @param {{
 *   nextCollection?: Array,
 *   allowInSafeMode?: boolean
 * }} [options]
 * @returns {Promise<{ item: object, payloadVersion: number, nextCollection?: Array }>}
 */
export function queueEntityItemUpsert(businessId, field, item, options = {}) {
  const n = normalizeBusinessStorageId(businessId);
  if (n == null || n === '') {
    return Promise.reject(new Error('queueEntityItemUpsert: missing businessId'));
  }
  if (!field || !item || typeof item !== 'object') {
    return Promise.reject(new Error('queueEntityItemUpsert: field and item required'));
  }

  try {
    assertBusinessPayloadWriteAllowed(options);
  } catch (err) {
    return Promise.reject(err);
  }

  const prev = chains.get(n) || Promise.resolve();
  const run = prev
    .then(() => applyEntityItemUpsertWithRetry(n, field, item, options))
    .catch((err) => {
      console.error('[businessPayloadWrite] entity item upsert failed:', err?.message || err);
      throw err;
    });

  chains.set(
    n,
    run.catch(() => {})
  );
  return run;
}

async function applyEntityItemUpsertWithRetry(n, field, item, options) {
  const maxAttempts = isBusinessPayloadOptimisticLockEnabled()
    ? MAX_BUSINESS_WRITE_RETRIES
    : 1;

  let lastConflict = null;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const isLastAttempt = attempt === maxAttempts - 1;
    try {
      return await applyEntityItemUpsertOnce(n, field, item, {
        ...options,
        skipOptimisticLock: isLastAttempt && maxAttempts > 1
      });
    } catch (err) {
      if (err instanceof BusinessPayloadWriteConflict) {
        lastConflict = err;
        continue;
      }
      throw err;
    }
  }
  throw lastConflict || new BusinessPayloadWriteConflict();
}

async function applyEntityItemUpsertOnce(n, field, item, options) {
  const { isEntityStoreNoKvBlobEnabled, readBusinessPayloadMeta, reloadFreshBusinessPayloadMetaFromIdb } =
    await import('./businessPayloadMeta');
  const { isEntityStoreEnabled } = await import('./entityAggregateStore');

  if (!isEntityStoreEnabled() || !isEntityStoreNoKvBlobEnabled()) {
    throw new Error(
      'queueEntityItemUpsert requires ENTITY_STORE + NO_KV_BLOB; use queueBusinessPayloadFieldWrite'
    );
  }

  if (isBusinessPayloadOptimisticLockEnabled()) {
    await reloadFreshBusinessPayloadMetaFromIdb(n);
  }

  const metaBefore = await readBusinessPayloadMeta(n);
  const lockVersion = metaBefore.payloadVersion;
  const nextVersion =
    Number.isFinite(lockVersion) && lockVersion >= 0 ? lockVersion + 1 : 1;
  const payloadUpdatedAt = new Date().toISOString();

  if (isBusinessPayloadOptimisticLockEnabled() && !options.skipOptimisticLock) {
    const currentMeta = await reloadFreshBusinessPayloadMetaFromIdb(n);
    if (currentMeta.payloadVersion !== lockVersion) {
      throw new BusinessPayloadWriteConflict();
    }
  }

  await runAtomicEntityItemPersist({
    businessId: n,
    aggregate: field,
    item,
    payloadMeta: {
      payloadVersion: nextVersion,
      payloadUpdatedAt
    }
  });

  const changedFields = [field];
  const cachePayload = options.nextCollection
    ? { [field]: options.nextCollection }
    : null;

  try {
    if (cachePayload) {
      await refreshReadCachesForFields(n, changedFields, cachePayload);
    } else {
      await refreshReadCachesForFields(n, changedFields);
    }
  } catch (err) {
    console.warn(
      '[businessPayloadWrite] entity item read-cache refresh failed:',
      err?.message || err
    );
  }

  publishDomainEvent(DOMAIN_EVENTS.BUSINESS_PAYLOAD_UPDATED, {
    businessId: n,
    changedFields
  });

  const { broadcastEntityPayloadInvalidation } = await import('./kvCacheSync');
  broadcastEntityPayloadInvalidation(n, changedFields);

  try {
    const { scheduleSequenceSyncAfterWrite } = await import('./sequenceSyncAfterWrite');
    scheduleSequenceSyncAfterWrite(n, changedFields);
  } catch {
    /* non-blocking */
  }

  return {
    item,
    payloadVersion: nextVersion,
    nextCollection: options.nextCollection
  };
}

function normalizeCustomerId(customer) {
  return customer?.customerId || customer?.id || null;
}

function findCustomerByPhone(customers, phone) {
  return customers.find((c) => phonesMatch(c.phone, phone));
}

async function createCheckoutCustomerRecord(customers, { name, phone }, generateClientIdFn) {
  if (typeof generateClientIdFn !== 'function') return null;
  const resolvedId = await Promise.resolve(generateClientIdFn());
  customers.push({
    customerId: resolvedId,
    name: name || '',
    phone: phone || '',
    email: '',
    address: '',
    totalOrders: 0,
    totalSpent: 0,
    lastOrderDate: null,
    joinedDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  return resolvedId;
}

/**
 * Kundenliste + aufgelöste Verkaufs-Kundenfelder (ein Schreibvorgang mit dem Verkauf).
 */
async function mergeCheckoutCustomerIntoParsed(
  parsed,
  { customerName = '', customerPhone = '', customerId = null },
  generateClientIdFn = generateClientId
) {
  const name = (customerName || '').trim();
  const phone = (customerPhone || '').trim();
  let resolvedId = customerId || null;
  const customers = Array.isArray(parsed.customers) ? [...parsed.customers] : [];

  if (resolvedId) {
    const idx = customers.findIndex((c) => normalizeCustomerId(c) === resolvedId);
    if (idx >= 0) {
      customers[idx] = {
        ...customers[idx],
        ...(name ? { name } : {}),
        ...(phone ? { phone } : { phone: customers[idx].phone || '' })
      };
    }
  } else if (name) {
    const byName = customers.find(
      (c) => (c.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (byName) {
      resolvedId = normalizeCustomerId(byName);
      if (phone) {
        const idx = customers.findIndex((c) => normalizeCustomerId(c) === resolvedId);
        if (idx >= 0) {
          customers[idx] = { ...customers[idx], phone };
        }
      }
    } else {
      resolvedId = await createCheckoutCustomerRecord(
        customers,
        { name, phone },
        generateClientIdFn
      );
    }
  } else if (phone) {
    const byPhone = findCustomerByPhone(customers, phone);
    if (byPhone) {
      resolvedId = normalizeCustomerId(byPhone);
    } else {
      resolvedId = await createCheckoutCustomerRecord(
        customers,
        { name: '', phone },
        generateClientIdFn
      );
    }
  }

  let resolvedName = name;
  let resolvedPhone = phone;
  if (resolvedId) {
    const match = customers.find((c) => normalizeCustomerId(c) === resolvedId);
    if (match) {
      resolvedName = (name || match.name || '').trim();
      resolvedPhone = (phone || match.phone || '').trim();
    }
  }

  parsed.customers = customers;

  return {
    customerName: resolvedName,
    customerPhone: resolvedPhone,
    customerId: resolvedId,
    customers
  };
}

/**
 * Kunde + Verkaufs-Kundenfelder atomar vor Checkout persistieren.
 * @returns {Promise<{ customerName: string, customerPhone: string, customerId: string|null, customers: Array }>}
 */
export async function persistCheckoutCustomer(
  businessId,
  customerOpts,
  saleRef = null,
  generateClientIdFn = null
) {
  let resolved = null;

  await queueBusinessPayloadWrite(
    businessId,
    async (parsed) => {
    resolved = await mergeCheckoutCustomerIntoParsed(
      parsed,
      customerOpts,
      generateClientIdFn || generateClientId
    );
    if (saleRef) {
      saleRef.customerName = resolved.customerName;
      saleRef.customerPhone = resolved.customerPhone;
      saleRef.customerId = resolved.customerId;
    }
    return parsed;
  },
    { allowInSafeMode: true, authoritativeFields: ['customers'] }
  );

  return resolved;
}

/** ADR-0013: Créances bei explizitem Kredit/Rest — domain/customers/balance */
async function applyCheckoutCustomerBalanceIfNeeded(parsed, sale) {
  const {
    planCustomerBalanceDeltaFromSale,
    applyCustomerBalanceDelta
  } = await import('../../domain/customers/balance');
  const plan = planCustomerBalanceDeltaFromSale(sale, parsed.customerDebts);
  if (!plan) return null;
  return applyCustomerBalanceDelta(parsed, plan);
}

/**
 * Verkauf in sales[] mergen (nach Inventory, vor React-State).
 */
export async function persistCompletedSale(businessId, saleData, options = {}) {
  const {
    generateClientIdFn = generateClientId,
    skipFinalizeJournal = false,
    enqueueSaleSync = true,
    saleSyncMutationId = null
  } = options;
  const { buildSaleCompletedFact } = await import('../../domain/sales/checkout');
  let sale = buildCompletedSaleRecord({
    ...saleData,
    total: Number(saleData.total ?? saleData.totalAmount ?? saleData.grandTotal ?? 0),
    totalAmount: Number(saleData.total ?? saleData.totalAmount ?? saleData.grandTotal ?? 0)
  });

  const saleJournal = skipFinalizeJournal
    ? null
    : appendSaleFinalizeJournalPending(businessId, {
        orderId: sale.orderId,
        saleId: sale.id,
        clientMutationId: sale.clientMutationId,
        totalAmount: sale.totalAmount,
        saleData
      });

  let finalSales = [];
  let finalCustomers = [];
  let balanceChange = null;

  try {
    await queueBusinessPayloadWrite(
      businessId,
      async (parsed) => {
        const hasCustomerInput =
          (saleData.customerName || '').trim() ||
          (saleData.customerPhone || '').trim() ||
          saleData.customerId;

        if (hasCustomerInput) {
          const customerFields = await mergeCheckoutCustomerIntoParsed(
            parsed,
            {
              customerName: saleData.customerName,
              customerPhone: saleData.customerPhone,
              customerId: saleData.customerId
            },
            generateClientIdFn
          );
          sale = {
            ...sale,
            customerName: customerFields.customerName,
            customerPhone: customerFields.customerPhone,
            customerId: customerFields.customerId
          };
          finalCustomers = customerFields.customers;
        } else if (Array.isArray(parsed.customers)) {
          finalCustomers = parsed.customers;
        }

        balanceChange = await applyCheckoutCustomerBalanceIfNeeded(parsed, sale);

        const sales = Array.isArray(parsed.sales) ? [...parsed.sales] : [];
        const idx = sales.findIndex(
          (s) =>
            (sale.orderId && s.orderId === sale.orderId) ||
            (sale.id && s.id === sale.id)
        );
        if (idx >= 0) {
          sales[idx] = { ...sales[idx], ...sale, status: 'completed' };
        } else {
          sales.push(sale);
        }
        parsed.sales = sales;
        finalSales = sales;
        return parsed;
      },
      {
        allowInSafeMode: true,
        authoritativeFields: ['sales', 'customers', 'customerDebts'],
        outboxEntries: enqueueSaleSync
          ? saleSyncOutboxEntriesResolver(businessId, sale, saleSyncMutationId)
          : undefined
      }
    );
  } catch (err) {
    if (saleJournal?.id) {
      markSaleFinalizeJournalFailed(businessId, saleJournal.id, err?.message || String(err));
    }
    throw err;
  }

  if (saleJournal?.id) {
    markSaleFinalizeJournalCommitted(businessId, saleJournal.id, sale);
  }

  publishDomainEvent(
    DOMAIN_EVENTS.SALE_COMPLETED,
    buildSaleCompletedFact(sale, { businessId, finalSales })
  );
  if (balanceChange) {
    publishDomainEvent(DOMAIN_EVENTS.CUSTOMER_BALANCE_CHANGED, {
      businessId,
      ...balanceChange
    });
  }

  return { sale, finalSales, customers: finalCustomers, balanceChange };
}

function buildCompletedSaleRecord(saleData) {
  const total = Number(
    saleData.total ?? saleData.totalAmount ?? saleData.grandTotal ?? 0
  );
  // Drop ephemeral planning context — recipeId/version stay on line products[]; FIFO uses movements.
  const {
    recipes: _recipesCtx,
    catalogProducts: _catalogCtx,
    ...saleWithoutPlanCtx
  } = saleData || {};
  let sale = {
    ...saleWithoutPlanCtx,
    status: 'completed',
    total,
    totalAmount: total,
    workerId:
      saleData.workerId ??
      saleData.worker?.id ??
      saleData.cashierId ??
      null,
    workerName:
      saleData.workerName ??
      saleData.worker?.name ??
      saleData.cashierName ??
      ''
  };
  if (!sale.id && sale.orderId) {
    sale.id = `sale_${sale.orderId}`;
  }
  return stampSaleCompletionFields(sale);
}

/**
 * P1 — Atomarer Checkout: Lots + stockData + sales[] in einem RMW.
 * Verhindert „Verkauf gespeichert, Lots nicht reduziert“ innerhalb eines Tabs.
 */
export async function persistCheckoutAtomically(businessId, saleData, options = {}) {
  const {
    generateClientIdFn = generateClientId,
    skipFinalizeJournal = false,
    enqueueSaleSync = true,
    saleSyncMutationId = null
  } = options;

  if (!saleData.orderId) {
    const { generateVenteId } = await import('../../utils/frenchIdGenerator');
    saleData.orderId = await generateVenteId(businessId);
  }
  if (!saleData.id && saleData.orderId) {
    saleData.id = `sale_${saleData.orderId}`;
  }

  try {
    const { readSalesSyncForBusiness } = await import('../../utils/salesReadSource');
    const existing = readSalesSyncForBusiness(businessId) || [];
    const taken = existing.some(
      (s) =>
        String(s?.orderId || '') === String(saleData.orderId) ||
        String(s?.id || '') === String(saleData.id)
    );
    if (taken) {
      const { generateVenteId } = await import('../../utils/frenchIdGenerator');
      saleData.orderId = await generateVenteId(businessId);
      saleData.id = `sale_${saleData.orderId}`;
    }
  } catch {
    /* non-blocking duplicate guard */
  }

  delete saleData.previewOrderId;

  const {
    planSaleLotDeductions,
    refreshStockDataInParsed,
    dispatchCheckoutInventoryEvents
  } = await import('../../domain/inventory/consume');
  const { buildSaleCompletedFact } = await import('../../domain/sales/checkout');

  let sale = buildCompletedSaleRecord({
    ...saleData,
    lotInventoryApplied: true,
    lotInventoryAppliedAt:
      saleData.lotInventoryAppliedAt || new Date().toISOString()
  });

  const saleJournal = skipFinalizeJournal
    ? null
    : appendSaleFinalizeJournalPending(businessId, {
        orderId: sale.orderId,
        saleId: sale.id,
        clientMutationId: sale.clientMutationId,
        totalAmount: sale.totalAmount,
        saleData: sale
      });

  let finalSales = [];
  let finalCustomers = [];
  let inventoryPlan = null;
  let balanceChange = null;

  try {
    await queueBusinessPayloadWrite(
      businessId,
      async (parsed) => {
        if (!saleData.location) {
          saleData.location = 'Principal';
        }

        // Full read if partial would drop recipes/products (dish → ingredient expand).
        // also inject shell recipes when sale only carried empty [] from React state.
        const { resolveRecipesForPlanning } = await import(
          '../../domain/restaurant/dishIncludedStock'
        );
        const recipes = resolveRecipesForPlanning(saleData.recipes, parsed?.recipes);
        if (recipes.length > 0) {
          saleData.recipes = recipes;
        }

        const plan = planSaleLotDeductions(saleData, parsed);
        if (!plan.success) {
          throw new Error(plan.error || 'Inventory planning failed');
        }

        inventoryPlan = plan;
        // ADR-0016 Phase 4: movements → lot projection (no pre-mutate lots)
        const {
          applyInventoryMovements,
          buildSaleMovementsFromPlan,
          buildSaleCostSnapshot,
          generateCorrelationId
        } = await import('../../domain/inventory/movements');
        const inventoryCorrelationId =
          saleData.inventoryCorrelationId ||
          saleData.correlationId ||
          generateCorrelationId(sale.orderId || sale.id);
        const costSnapshot = buildSaleCostSnapshot(
          { ...saleData, inventoryCorrelationId },
          plan
        );
        const { shouldPersistSaleInventoryCost } = await import(
          '../../domain/restaurant/restaurantContext'
        );
        const isRestaurantCost = shouldPersistSaleInventoryCost(parsed, options);
        sale = {
          ...sale,
          inventoryCorrelationId,
          inventoryCost: Number(costSnapshot.inventoryCost) || 0,
          inventoryCostFrozen: true,
          costMethod: costSnapshot.costMethod,
          costMethodVersion: costSnapshot.costMethodVersion,
          costSnapshot,
          ...(isRestaurantCost ? { restaurantInventoryCost: true } : {})
        };

        applyInventoryMovements(
          parsed,
          buildSaleMovementsFromPlan(
            { ...saleData, inventoryCorrelationId, ...sale },
            plan
          ),
          {
            businessId,
            defaultCorrelationId: inventoryCorrelationId,
            actorId: saleData.workerId || saleData.actorId || sale.workerId || sale.actorId,
            sessionId:
              saleData.sessionId ||
              saleData.cashRegisterSessionId ||
              sale.sessionId ||
              sale.cashRegisterSessionId,
            storeId: saleData.storeId || sale.storeId || null
          }
        );
        refreshStockDataInParsed(parsed, plan.touchedStockKeys);

        const hasCustomerInput =
          (saleData.customerName || '').trim() ||
          (saleData.customerPhone || '').trim() ||
          saleData.customerId;

        if (hasCustomerInput) {
          const customerFields = await mergeCheckoutCustomerIntoParsed(
            parsed,
            {
              customerName: saleData.customerName,
              customerPhone: saleData.customerPhone,
              customerId: saleData.customerId
            },
            generateClientIdFn
          );
          sale = {
            ...sale,
            customerName: customerFields.customerName,
            customerPhone: customerFields.customerPhone,
            customerId: customerFields.customerId
          };
          finalCustomers = customerFields.customers;
        } else if (Array.isArray(parsed.customers)) {
          finalCustomers = parsed.customers;
        }

        balanceChange = await applyCheckoutCustomerBalanceIfNeeded(parsed, sale);

        const sales = Array.isArray(parsed.sales) ? [...parsed.sales] : [];
        const idx = sales.findIndex(
          (s) =>
            (sale.orderId && s.orderId === sale.orderId) ||
            (sale.id && s.id === sale.id)
        );
        if (idx >= 0) {
          sales[idx] = { ...sales[idx], ...sale, status: 'completed' };
        } else {
          sales.push(sale);
        }
        parsed.sales = sales;
        finalSales = sales;
        return parsed;
      },
      {
        allowInSafeMode: true,
        // Must load recipes + catalog products for dish→ingredient expand (not entity-only fields).
        partialEntityRead: false,
        authoritativeFields: [
          'sales',
          'customers',
          'customerDebts',
          'lots',
          'stockData',
          'stockMovements',
          'inventoryLedgerMeta',
          'stockMovementsArchive',
          'stockSnapshots'
        ],
        outboxEntries: enqueueSaleSync
          ? saleSyncOutboxEntriesResolver(businessId, sale, saleSyncMutationId)
          : undefined
      }
    );
  } catch (err) {
    if (saleJournal?.id) {
      markSaleFinalizeJournalFailed(
        businessId,
        saleJournal.id,
        err?.message || String(err)
      );
    }
    throw err;
  }

  if (saleJournal?.id) {
    markSaleFinalizeJournalCommitted(businessId, saleJournal.id, sale);
  }

  if (inventoryPlan) {
    dispatchCheckoutInventoryEvents(inventoryPlan);
  }

  publishDomainEvent(
    DOMAIN_EVENTS.SALE_COMPLETED,
    buildSaleCompletedFact(sale, { businessId, finalSales })
  );
  if (balanceChange) {
    publishDomainEvent(DOMAIN_EVENTS.CUSTOMER_BALANCE_CHANGED, {
      businessId,
      ...balanceChange
    });
  }

  return {
    sale,
    finalSales,
    customers: finalCustomers,
    balanceChange,
    inventory: {
      success: true,
      totalCost: inventoryPlan?.totalCost ?? 0,
      results: inventoryPlan?.results ?? [],
      lotInventoryApplied: true
    }
  };
}

/**
 * P1 — Atomarer Vente-/Zeilen-Storno: Lots restore + stockData + movements + sale + créance.
 */
export async function persistSaleVoidAtomically(businessId, voidRequest = {}) {
  const {
    saleId,
    mode = 'full',
    lines = [],
    reason = '',
    actor = null
  } = voidRequest;

  if (!businessId) {
    return { success: false, error: 'business_id_missing' };
  }
  if (saleId == null || saleId === '') {
    return { success: false, error: 'sale_id_missing' };
  }

  const {
    planSaleVoid,
    applySaleVoidToParsed,
    dispatchSaleVoidInventoryEvents,
    buildSaleVoidedFact,
    adjustSessionsAfterSaleVoid
  } = await import('../../domain/sales/storno');

  let nextSale = null;
  let plan = null;
  let balanceChange = null;
  let finalSales = [];

  try {
    await queueBusinessPayloadWrite(
      businessId,
      async (parsed) => {
        const sales = Array.isArray(parsed.sales) ? parsed.sales : [];
        const sid = String(saleId);
        const sale = sales.find(
          (s) => String(s?.id) === sid || String(s?.orderId) === sid
        );
        if (!sale) {
          throw Object.assign(new Error('sale_not_found'), { code: 'sale_not_found' });
        }

        plan = planSaleVoid(sale, parsed, {
          mode: mode === 'lines' ? 'lines' : 'full',
          lines
        });
        if (!plan.success) {
          throw Object.assign(new Error(plan.error || 'void_plan_failed'), {
            code: plan.error,
            detail: plan
          });
        }

        const applied = applySaleVoidToParsed(parsed, sale, plan, {
          reason,
          actor,
          at: new Date().toISOString(),
          businessId
        });
        nextSale = applied.sale;
        balanceChange = applied.balanceChange;
        finalSales = parsed.sales;
        return parsed;
      },
      {
        allowInSafeMode: true,
        authoritativeFields: [
          'sales',
          'customerDebts',
          'lots',
          'stockData',
          'stockMovements',
          'inventoryLedgerMeta',
          'stockMovementsArchive',
          'stockSnapshots'
        ],
        // ADR-0017 W2: re-upsert final sale state (void/partial) via same sale_sync path
        outboxEntries: saleSyncOutboxEntriesResolver(businessId, {
          id: saleId,
          orderId: saleId
        })
      }
    );
  } catch (err) {
    const code = err?.code || err?.message || 'void_failed';
    return {
      success: false,
      error: code,
      detail: err?.detail || null,
      message: err?.message || String(err)
    };
  }

  if (plan) {
    dispatchSaleVoidInventoryEvents(plan);
  }

  try {
    const { scheduleAutomaticCloudSync } = await import('../../services/parallelCloudFieldSync');
    scheduleAutomaticCloudSync(businessId, { reason: 'sale_void' });
  } catch {
    /* optional */
  }

  try {
    adjustSessionsAfterSaleVoid(businessId, nextSale, {
      fullyCancelled: Boolean(plan?.willCancelFully)
    });
  } catch (sessionErr) {
    console.warn(
      '[persistSaleVoidAtomically] session adjust failed:',
      sessionErr?.message || sessionErr
    );
  }

  // Kassenjournal: Sale-Tx voiden / Betrag anpassen (kein Phantom-Income nach Storno)
  try {
    const { markCashRegisterSalesVoided } = await import('../../services/cashRegisterUpdateService');
    const { resolveSaleRegisterIncome } = await import('../../domain/sales/checkout');
    const paidRemaining = plan?.willCancelFully
      ? 0
      : resolveSaleRegisterIncome(nextSale).amount;
    markCashRegisterSalesVoided(businessId, nextSale?.id || nextSale?.orderId || saleId, {
      fullyCancelled: Boolean(plan?.willCancelFully),
      amount: plan?.willCancelFully ? null : paidRemaining
    });
  } catch (journalErr) {
    console.warn(
      '[persistSaleVoidAtomically] cash register void mark failed:',
      journalErr?.message || journalErr
    );
  }

  publishDomainEvent(
    DOMAIN_EVENTS.SALE_VOIDED,
    buildSaleVoidedFact(nextSale, plan, { businessId, finalSales, reason, actor })
  );
  publishDomainEvent(DOMAIN_EVENTS.SALES_UPDATED, {
    businessId,
    saleId: nextSale?.id || nextSale?.orderId,
    action: plan?.willCancelFully ? 'void' : 'partial_void'
  });
  if (balanceChange) {
    publishDomainEvent(DOMAIN_EVENTS.CUSTOMER_BALANCE_CHANGED, {
      businessId,
      ...balanceChange
    });
  }

  return {
    success: true,
    sale: nextSale,
    plan,
    finalSales,
    balanceChange,
    inventory: {
      success: true,
      restores: plan?.restores || []
    }
  };
}

function normalizePurchasesArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return Object.values(raw);
  return [];
}

/**
 * Achats + Produkte atomar (verhindert Partial-Overwrite nur purchases).
 * @param {string} businessId
 * @param {{ purchases: Array, products?: Array }} payload
 */
export async function persistPurchasesAndProducts(businessId, payload, options = {}) {
  const { assertActorPermission } = await import('../../domain/security/actorGuard');
  assertActorPermission('purchases', 'persistPurchasesAndProducts', { businessId });

  const { purchases, products } = payload;
  let finalPurchases = [];
  let finalProducts = products;

  await queueBusinessPayloadWrite(
    businessId,
    (parsed) => {
      const existing = normalizePurchasesArray(parsed.purchases);
      const incoming = Array.isArray(purchases)
        ? purchases
        : normalizePurchasesArray(purchases);
      parsed.purchases = mergePurchasesById(existing, incoming);
      finalPurchases = parsed.purchases;
      if (products) {
        parsed.products = products;
        finalProducts = products;
      }
      return parsed;
    },
    {
      authoritativeFields: [
        'purchases',
        ...(products ? ['products'] : [])
      ],
      outboxEntries: options.outboxEntries
    }
  );

  publishDomainEvent(DOMAIN_EVENTS.PURCHASES_UPDATED, {
    businessId,
    purchases: finalPurchases,
    products: finalProducts
  });

  // Hybrid: product row changes from achat must reach cashier catalog
  if (Array.isArray(finalProducts) && finalProducts.length > 0) {
    try {
      const { enqueueHybridEntitySync } = await import('../../services/hybridCatalogSync');
      const stamp = new Date().toISOString();
      for (const product of finalProducts) {
        if (!product?.id) continue;
        void enqueueHybridEntitySync(businessId, 'product', {
          ...product,
          updatedAt: product.updatedAt || stamp
        });
      }
    } catch {
      /* hybrid optional */
    }
  }

  return { purchases: finalPurchases, products: finalProducts };
}

/**
 * Mehrere Payload-Felder atomar (z. B. Teilzahlung: supermarketPayments + supermarketInvoices).
 * @param {string} businessId
 * @param {Record<string, unknown>} fields
 */
export async function persistBusinessFieldsBatch(businessId, fields) {
  const entries = Object.entries(fields || {}).filter(([, v]) => v !== undefined);
  if (!businessId || entries.length === 0) return;

  const keys = entries.map(([k]) => k);
  await queueBusinessPayloadWrite(
    businessId,
    (parsed) => {
      for (const [field, value] of entries) {
        parsed[field] = value;
      }
      return parsed;
    },
    { authoritativeFields: keys }
  );
}

/**
 * Teilzahlung Lieferantenrechnung — Zahlung + Rechnungsstatus atomar.
 */
export async function persistSupermarketPartialPayment(
  businessId,
  { supermarketPayments, supermarketInvoices }
) {
  const { assertActorPermission } = await import('../../domain/security/actorGuard');
  assertActorPermission('payments', 'persistSupermarketPartialPayment', { businessId });

  // Atomic write already awaits the business chain + KV flush for this persist.
  await persistBusinessFieldsBatch(businessId, {
    supermarketPayments,
    supermarketInvoices
  });
}

/** Dépense + lignes expense_payment atomiques (caisse déjà mise à jour avant l'appel). */
export async function persistExpenseWithPayments(
  businessId,
  { expenses, supermarketPayments }
) {
  const { assertActorPermission } = await import('../../domain/security/actorGuard');
  assertActorPermission('expenses', 'persistExpenseWithPayments', { businessId });

  await persistBusinessFieldsBatch(businessId, {
    expenses,
    supermarketPayments
  });
}
